import { Routes, Route, useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import Shell from "@/layout/Shell";
import { useFetch } from "@/lib/useFetch";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import Badge from "@/components/Badge";
import EmptyState from "@/components/EmptyState";
import StatCard from "@/components/StatCard";
import { fmtDate, cn, EVENT_TYPES } from "@/lib/utils";
import {
  Megaphone, Plus, Building2, ChevronRight, Globe, Save,
  QrCode, Award, Users, Clock, Trash2, ExternalLink, Copy,
  CheckCircle2, Send, MessageSquare,
  X, AlertTriangle, FileDown,
} from "lucide-react";

/* ── Event list ──────────────────────────────────────────────────── */
function EventList() {
  const nav  = useNavigate();
  const standalone = useFetch(() => api.myStandaloneEvents().then(r => r?.events || []), []);
  const bookings   = useFetch(() => api.myBookings().then(r => r?.bookings || []), []);
  const confirmed  = (bookings.data || []).filter(b => b.status === "confirmed");

  return (
    <Shell role="portal" title="Host an event" subtitle="Create event pages, manage registrations"
      actions={<Button size="sm" className="gap-1.5" onClick={() => nav("/portal/events/new")}><Plus className="h-3.5 w-3.5" />New standalone</Button>}
    >
      <div className="grid sm:grid-cols-2 gap-4 mb-6">
        <button onClick={() => nav("/portal/events/new")}
          className="surface rounded-2xl p-5 text-left group card-hover">
          <div className="w-11 h-11 rounded-xl bg-blue-600/10 border border-blue-600/15 flex items-center justify-center mb-3 group-hover:bg-blue-600/15 transition-colors">
            <Megaphone className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="font-bold text-[15px]">Create standalone event</div>
          <div className="text-xs text-muted-foreground mt-1">Set your own date & venue — no hall booking needed</div>
        </button>
        <div className="surface rounded-2xl p-5">
          <div className="w-11 h-11 rounded-xl bg-emerald-500/10 border border-emerald-500/15 flex items-center justify-center mb-3">
            <Building2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div className="font-bold text-[15px]">From hall booking</div>
          <div className="text-xs text-muted-foreground mt-1 mb-3">Publish an event page for a confirmed booking</div>
          {confirmed.length === 0 ? (
            <p className="text-xs text-muted-foreground">No confirmed hall bookings found.</p>
          ) : (
            <Select onValueChange={bid => nav(`/portal/events/booking/${bid}`)}>
              <SelectTrigger className="text-xs h-9"><SelectValue placeholder="Select a booking…" /></SelectTrigger>
              <SelectContent>
                {confirmed.map(b => (
                  <SelectItem key={b.bookingID} value={b.bookingID}>
                    {b.eventName || b.bookingID} · {fmtDate(b.date)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {(standalone.data || []).length > 0 && (
        <div>
          <div className="overline mb-3">My standalone events</div>
          <div className="space-y-2">
            {(standalone.data || []).map(ev => (
              <Link key={ev.eventID} to={`/portal/events/manage/${ev.eventID}`}
                className="surface rounded-2xl p-4 flex items-center gap-3.5 card-hover">
                <div className="w-11 h-11 rounded-xl bg-violet-500/10 border border-violet-500/15 flex items-center justify-center shrink-0">
                  <Megaphone className="h-4.5 w-4.5 text-violet-600 dark:text-violet-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate text-foreground">{ev.title}</div>
                  <div className="text-xs text-muted-foreground">{fmtDate(ev.eventDate)} · {ev.eventType?.replace(/_/g, " ")}</div>
                </div>
                <Badge status={ev.visibility} />
                <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
              </Link>
            ))}
          </div>
        </div>
      )}
    </Shell>
  );
}

/* ── Profile form ─────────────────────────────────────────────────── */
const REG_FIELDS = [
  { id: "name",       label: "Full name",   required: true },
  { id: "email",      label: "Email",       required: true },
  { id: "usn",        label: "USN",         required: false },
  { id: "college_id", label: "College ID",  required: false },
  { id: "dept",       label: "Department",  required: false },
  { id: "year",       label: "Year / Sem",  required: false },
  { id: "phone",      label: "Phone",       required: false },
];

function ProfileForm({ eventID, bookingID, initial = {} }) {
  const isStandalone = !bookingID;
  const [busy,    setBusy]    = useState(false);
  const [savedID, setSavedID] = useState(eventID || null);
  const [copied,  setCopied]  = useState(false);

  const [form, setForm] = useState({
    eventType: "seminar", title: "", tagline: "", description: "",
    speakerName: "", speakerDesg: "", speakerPhoto: "", bannerUrl: "",
    audience: "all", visibility: "internal", deptFilter: "",
    eventDate: "", timeSlot: "", venueText: "",
    regEnabled: false, seatCap: 100, approvalMode: "auto",
    regOpenAt: "", regCloseAt: "", confirmMsg: "",
    formFields: ["name","email","usn","dept"],
    customQs: [],
    agenda: [], coOrg1: "", coOrg2: "", coOrg3: "",
    tags: "", extLink: "", waGroupLink: "",
    signatory: "Dr. Jayaprakash M K", signatoryRole: "Principal, BMSIT&M",
    coord1Name: "", coord1Phone: "", coord1Email: "",
    coord2Name: "", coord2Phone: "", coord2Email: "",
  });

  useEffect(() => {
    if (!initial || Object.keys(initial).length < 2) return;
    setForm(p => ({
      ...p, ...initial,
      tags:    Array.isArray(initial.tags) ? initial.tags.join(", ") : (initial.tags || ""),
      coOrg1:  (initial.coOrganisers || [])[0] || "",
      coOrg2:  (initial.coOrganisers || [])[1] || "",
      coOrg3:  (initial.coOrganisers || [])[2] || "",
      agenda:  Array.isArray(initial.agenda) ? initial.agenda : [],
      formFields: initial.formFields?.map(f => f.id || f) || ["name","email","usn","dept"],
      coord1Name:  (initial.coordinators || [])[0]?.name  || "",
      coord1Phone: (initial.coordinators || [])[0]?.phone || "",
      coord1Email: (initial.coordinators || [])[0]?.email || "",
      coord2Name:  (initial.coordinators || [])[1]?.name  || "",
      coord2Phone: (initial.coordinators || [])[1]?.phone || "",
      coord2Email: (initial.coordinators || [])[1]?.email || "",
    }));
    if (initial.bookingID || initial.eventID) setSavedID(initial.bookingID || initial.eventID);
  }, []); // eslint-disable-line

  const f = k => v => setForm(p => ({ ...p, [k]: typeof v === "object" && v?.target ? v.target.value : v }));

  const addSession    = () => setForm(p => ({ ...p, agenda: [...p.agenda, { time: "", title: "", speaker: "" }] }));
  const removeSession = i  => setForm(p => ({ ...p, agenda: p.agenda.filter((_, j) => j !== i) }));
  const setSession    = (i, k, v) => setForm(p => ({ ...p, agenda: p.agenda.map((s, j) => j === i ? { ...s, [k]: v } : s) }));

  const toggleField = (id) => setForm(p => ({
    ...p, formFields: p.formFields.includes(id) ? p.formFields.filter(x => x !== id) : [...p.formFields, id]
  }));

  const addCq = () => setForm(p => ({ ...p, customQs: [...p.customQs, { question: "", type: "text", required: false }] }));
  const removeCq = i => setForm(p => ({ ...p, customQs: p.customQs.filter((_, j) => j !== i) }));
  const setCq = (i, k, v) => setForm(p => ({ ...p, customQs: p.customQs.map((q, j) => j === i ? { ...q, [k]: v } : q) }));

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (isStandalone && (!form.eventDate || !form.venueText.trim())) { toast.error("Date and venue are required"); return; }
    setBusy(true);
    const payload = {
      ...form,
      agenda:       form.agenda.filter(s => s.title),
      coOrganisers: [form.coOrg1, form.coOrg2, form.coOrg3].filter(Boolean),
      tags:         form.tags ? form.tags.split(",").map(t => t.trim()).filter(Boolean) : [],
      formFields:   form.formFields.map(id => ({ id })),
      coordinators: [
        form.coord1Name ? { name: form.coord1Name, phone: form.coord1Phone, email: form.coord1Email } : null,
        form.coord2Name ? { name: form.coord2Name, phone: form.coord2Phone, email: form.coord2Email } : null,
      ].filter(Boolean),
    };
    try {
      if (isStandalone) {
        const r = await api.saveStandaloneEvent(savedID || "", payload);
        if (!savedID) setSavedID(r.eventID);
        toast.success(r.isNew ? `Event created! ID: ${r.eventID}` : "Event saved");
      } else {
        await api.saveEventProfile(bookingID, payload);
        setSavedID(bookingID);
        toast.success("Event profile saved");
      }
    } catch(e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const eventUrl = savedID ? `${window.location.origin}/events/${savedID}` : null;
  const copyLink = () => { navigator.clipboard.writeText(eventUrl).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }); };

  return (
    <div className="space-y-5 max-w-2xl">
      {/* Standalone required fields */}
      {isStandalone && (
        <div className="surface rounded-2xl p-5 border border-amber-200 dark:border-amber-800/40 bg-amber-50/40 dark:bg-amber-950/20 space-y-3">
          <p className="text-sm font-semibold text-amber-700 dark:text-amber-400 flex items-center gap-2"><Clock className="h-4 w-4" />Date, time & venue</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>Event date *</Label><Input type="date" value={form.eventDate} onChange={f("eventDate")} /></div>
            <div className="space-y-1.5"><Label>Time slot *</Label><Input value={form.timeSlot} onChange={f("timeSlot")} placeholder="10:00 AM – 1:00 PM" /></div>
          </div>
          <div className="space-y-1.5"><Label>Venue *</Label><Input value={form.venueText} onChange={f("venueText")} placeholder="Seminar Hall A226, BMSIT&M" /></div>
        </div>
      )}

      {/* Basic info */}
      <div className="surface rounded-2xl p-5 space-y-4">
        <p className="overline">Basic info</p>
        <div className="grid grid-cols-4 gap-2">
          {EVENT_TYPES.map(t => (
            <button key={t.id} type="button" onClick={() => setForm(p => ({ ...p, eventType: t.id }))}
              className={cn("py-2 rounded-xl border text-[11px] font-medium transition-all",
                form.eventType === t.id ? "border-primary bg-primary/10 text-primary" : "border-border bg-white/40 dark:bg-white/5 hover:border-primary/40")}
            >{t.label}</button>
          ))}
        </div>
        <div className="space-y-1.5"><Label>Display title *</Label><Input value={form.title} onChange={f("title")} maxLength={200} placeholder="e.g. National Workshop on AI & ML" /></div>
        <div className="space-y-1.5"><Label>Tagline</Label><Input value={form.tagline} onChange={f("tagline")} placeholder="One line that makes people want to attend" /></div>
        <div className="space-y-1.5"><Label>Full description</Label><Textarea rows={4} value={form.description} onChange={f("description")} placeholder="Topics, prerequisites, what to bring…" /></div>
      </div>

      {/* Coordinators */}
      <div className="surface rounded-2xl p-5 space-y-4">
        <p className="overline">Event coordinators</p>
        <div className="space-y-3">
          {[["1",""],["2"," (optional)"]].map(([n, opt]) => (
            <div key={n} className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground">Coordinator {n}{opt}</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <Input value={form[`coord${n}Name`]} onChange={f(`coord${n}Name`)} placeholder="Name" />
                <Input type="tel" value={form[`coord${n}Phone`]} onChange={f(`coord${n}Phone`)} placeholder="Phone" />
                <Input type="email" value={form[`coord${n}Email`]} onChange={f(`coord${n}Email`)} placeholder="@bmsit.in" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Speaker + Banner */}
      <div className="surface rounded-2xl p-5 space-y-4">
        <p className="overline">Speaker / Chief guest</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Name</Label><Input value={form.speakerName} onChange={f("speakerName")} placeholder="Dr. / Prof. name" /></div>
          <div className="space-y-1.5"><Label>Designation</Label><Input value={form.speakerDesg} onChange={f("speakerDesg")} placeholder="Professor, IISc Bengaluru" /></div>
        </div>
        <div className="space-y-1.5"><Label>Speaker photo URL</Label><Input type="url" value={form.speakerPhoto} onChange={f("speakerPhoto")} placeholder="https://drive.google.com/uc?id=…" /></div>
        <div className="space-y-1.5"><Label>Banner image URL <span className="text-muted-foreground font-normal text-[11px]">(1200×630 px)</span></Label>
          <Input type="url" value={form.bannerUrl} onChange={f("bannerUrl")} placeholder="https://… or any public image URL" />
        </div>
      </div>

      {/* Audience */}
      <div className="surface rounded-2xl p-5 space-y-4">
        <p className="overline">Audience & visibility</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label>Target audience</Label>
            <Select value={form.audience} onValueChange={f("audience")}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {[["all","Students + Faculty (BMSIT)"],["students","Students only"],["faculty","Faculty only"],["public","Open to public"]].map(([v,l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Dept restriction <span className="text-muted-foreground font-normal text-[11px]">(blank=all)</span></Label>
            <Input value={form.deptFilter} onChange={f("deptFilter")} placeholder="CSE, AIML, ECE" />
          </div>
        </div>
        <div className="space-y-1.5"><Label>Visibility</Label>
          <div className="flex gap-2">
            {[["public","Public"],["internal","Internal (@bmsit.in)"],["private","Private (link only)"]].map(([v,l]) => (
              <button key={v} type="button" onClick={() => setForm(p => ({ ...p, visibility: v }))}
                className={cn("flex-1 py-2 rounded-xl text-xs font-medium border transition-all",
                  form.visibility === v ? "border-primary bg-primary/10 text-primary" : "border-border bg-white/40 dark:bg-white/5")}
              >{l}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Registration */}
      <div className="surface rounded-2xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <p className="overline">Registration</p>
          <div className="flex items-center gap-2">
            <Label className="text-xs">Enable</Label>
            <Switch checked={Boolean(form.regEnabled)} onCheckedChange={v => setForm(p => ({ ...p, regEnabled: v }))} />
          </div>
        </div>
        {form.regEnabled && (
          <div className="space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Seat capacity</Label><Input type="number" min={1} value={form.seatCap} onChange={e => setForm(p => ({ ...p, seatCap: Number(e.target.value) }))} /></div>
              <div className="space-y-1.5"><Label>Approval</Label>
                <Select value={form.approvalMode} onValueChange={f("approvalMode")}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="auto">Auto-confirm</SelectItem><SelectItem value="manual">Manual approval</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Opens</Label><Input type="datetime-local" value={form.regOpenAt} onChange={f("regOpenAt")} /></div>
              <div className="space-y-1.5"><Label>Closes</Label><Input type="datetime-local" value={form.regCloseAt} onChange={f("regCloseAt")} /></div>
            </div>
            <div className="space-y-1.5"><Label>Confirmation message</Label>
              <Input value={form.confirmMsg} onChange={f("confirmMsg")} placeholder="Thank you for registering! See you there." />
            </div>
            {/* Form field selector */}
            <div className="space-y-2">
              <Label className="text-xs">Registration form fields</Label>
              <div className="flex flex-wrap gap-2">
                {REG_FIELDS.map(field => (
                  <label key={field.id} className={cn("flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border cursor-pointer transition-all",
                    form.formFields.includes(field.id) ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:border-primary/40"
                  )}>
                    <input type="checkbox" checked={form.formFields.includes(field.id)} onChange={() => toggleField(field.id)} className="hidden" />
                    {field.label}{field.required ? " *" : ""}
                  </label>
                ))}
              </div>
            </div>
            {/* Custom questions */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs">Custom questions <span className="text-muted-foreground font-normal">(max 3)</span></Label>
                {form.customQs.length < 3 && (
                  <button onClick={addCq} className="text-xs text-primary hover:underline">+ Add question</button>
                )}
              </div>
              {form.customQs.map((q, i) => (
                <div key={i} className="glass rounded-xl p-3 space-y-2">
                  <div className="flex gap-2">
                    <Input value={q.question} onChange={e => setCq(i, "question", e.target.value)} placeholder="Question…" className="flex-1 text-xs h-8" />
                    <select value={q.type} onChange={e => setCq(i, "type", e.target.value)} className="h-8 text-xs rounded-lg border border-input bg-white/60 dark:bg-white/5 px-2">
                      <option value="text">Text</option><option value="yesno">Yes/No</option>
                    </select>
                    <button onClick={() => removeCq(i)} className="text-muted-foreground hover:text-destructive"><X className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Agenda */}
      <div className="surface rounded-2xl p-5 space-y-3">
        <div className="flex items-center justify-between">
          <p className="overline">Agenda / schedule</p>
          <button onClick={addSession} className="text-xs text-primary hover:underline flex items-center gap-1"><Plus className="h-3 w-3" />Add session</button>
        </div>
        {form.agenda.length === 0 && <p className="text-xs text-muted-foreground">No sessions. Sessions appear as a timeline on the public event page.</p>}
        <div className="space-y-2">
          {form.agenda.map((s, i) => (
            <div key={i} className="grid grid-cols-[80px_1fr_auto] sm:grid-cols-[90px_1fr_1fr_auto] gap-2 items-center">
              <Input type="time" value={s.time} onChange={e => setSession(i, "time", e.target.value)} className="text-xs font-mono h-9" />
              <Input value={s.title} onChange={e => setSession(i, "title", e.target.value)} placeholder="Session title" className="text-xs h-9" />
              <Input value={s.speaker || ""} onChange={e => setSession(i, "speaker", e.target.value)} placeholder="Speaker" className="text-xs h-9 hidden sm:flex" />
              <button onClick={() => removeSession(i)} className="text-muted-foreground hover:text-destructive shrink-0"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
        </div>
      </div>

      {/* Co-organisers */}
      <div className="surface rounded-2xl p-5 space-y-3">
        <p className="overline">Co-organisers <span className="text-muted-foreground normal-case font-normal text-[11px]">— can manage this event</span></p>
        <div className="space-y-2">
          {["coOrg1","coOrg2","coOrg3"].map((k, i) => (
            <Input key={k} value={form[k]} onChange={f(k)} placeholder={`Co-organiser ${i+1} email @bmsit.in`} type="email" />
          ))}
        </div>
      </div>

      {/* Tags & links */}
      <div className="surface rounded-2xl p-5 space-y-3">
        <p className="overline">Tags & links</p>
        <div className="space-y-1.5"><Label>Tags <span className="text-muted-foreground font-normal text-[11px]">(comma separated)</span></Label>
          <Input value={form.tags} onChange={f("tags")} placeholder="AI, Career, Research, Industry" />
        </div>
        <div className="space-y-1.5"><Label>External link</Label><Input type="url" value={form.extLink || ""} onChange={f("extLink")} placeholder="Zoom / Teams / website URL (optional)" /></div>
        <div className="space-y-1.5"><Label>WhatsApp group link <span className="text-muted-foreground font-normal text-[11px]">(shown on ticket)</span></Label>
          <Input type="url" value={form.waGroupLink || ""} onChange={f("waGroupLink")} placeholder="https://chat.whatsapp.com/…" />
        </div>
      </div>

      {/* Certificate signatory */}
      <div className="surface rounded-2xl p-5 space-y-3">
        <p className="overline">Certificate signatory</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Signing authority</Label><Input value={form.signatory} onChange={f("signatory")} placeholder="Dr. Jayaprakash M K" /></div>
          <div className="space-y-1.5"><Label>Designation</Label><Input value={form.signatoryRole} onChange={f("signatoryRole")} placeholder="Principal, BMSIT&M" /></div>
        </div>
      </div>

      {/* Save & share */}
      <div className="flex gap-3 flex-wrap items-center">
        <Button onClick={save} disabled={busy} className="gap-2 flex-1 sm:flex-none min-w-36">
          <Save className="h-4 w-4" />{busy ? "Saving…" : "Save & publish"}
        </Button>
        {eventUrl && (
          <>
            <a href={eventUrl} target="_blank" rel="noopener noreferrer"><Button variant="outline" className="gap-2"><ExternalLink className="h-4 w-4" />Preview</Button></a>
            <Button variant="outline" onClick={copyLink} className="gap-2">
              {copied ? <CheckCircle2 className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
              {copied ? "Copied!" : "Copy link"}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

/* ── Registrations tab ────────────────────────────────────────────── */
function Registrations({ bookingID }) {
  const { data, loading, reload } = useFetch(() => api.listRegs(bookingID), [bookingID]);
  const [q, setQ]           = useState("");
  const [filter, setFilter] = useState("all");
  const [broadcast, setBroadcast] = useState(false);
  const [bSubject, setBSubject]   = useState("");
  const [bBody,    setBBody]      = useState("");
  const [bBusy,    setBBusy]      = useState(false);
  const regs    = (data?.registrations || []).filter(r =>
    (filter === "all" || r.status === filter) &&
    (!q || [r.name, r.email, r.usn].join(" ").toLowerCase().includes(q.toLowerCase()))
  );
  const summary = data?.summary || {};

  const exportCSV = async () => {
    try {
      const d = await api.exportRegs(bookingID);
      const header = ["Reg ID","Name","Email","USN","College ID","Dept","Year","Phone","Status","Registered At"];
      const rows   = (d.registrations || []).map(r => [r.reg_id, r.name, r.email, r.usn, r.college_id, r.dept, r.year, r.phone, r.status, r.registered_at]);
      const csv    = [header, ...rows].map(r => r.map(c => `"${String(c || "").replace(/"/g, '""')}"`).join(",")).join("\n");
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
      a.download = `participants_${bookingID}.csv`; a.click();
    } catch(e) { toast.error(e.message); }
  };

  const bulkApprove = async (action) => {
    const ids = regs.filter(r => r.status === "pending_approval").map(r => r.regID);
    if (!ids.length) return;
    try { await api.bulkApprove(bookingID, ids, action); toast.success(`${ids.length} ${action}d`); reload(); }
    catch(e) { toast.error(e.message); }
  };

  const sendBroadcast = async () => {
    if (!bSubject || !bBody) { toast.error("Subject and message are required"); return; }
    setBBusy(true);
    try { await api.broadcast(bookingID, bSubject, bBody); toast.success("Message sent!"); setBroadcast(false); }
    catch(e) { toast.error(e.message); }
    finally { setBBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <StatCard label="Total"     value={data?.count || 0}      accent="#8B5CF6" />
        <StatCard label="Confirmed" value={summary.confirmed || 0} accent="#10B981" />
        <StatCard label="Pending"   value={summary.pending || 0}   accent="#F59E0B" />
        <StatCard label="Waitlist"  value={summary.waitlist || 0}  accent="#0038C8" />
        <StatCard label="Seats left" value={Math.max(0, (data?.registrations?.[0]?.seatCap || 0) - (summary.confirmed || 0) - (summary.pending || 0))} accent="#64748b" />
      </div>

      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-40">
          <Input placeholder="Search name, email, USN…" value={q} onChange={e => setQ(e.target.value)} className="pl-3" />
        </div>
        <select value={filter} onChange={e => setFilter(e.target.value)}
          className="h-11 text-sm rounded-xl border border-input bg-white/60 dark:bg-white/5 px-3 min-w-28">
          {[["all","All"],["confirmed","Confirmed"],["pending_approval","Pending"],["waitlist","Waitlist"],["cancelled","Cancelled"]].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        {(summary.pending || 0) > 0 && <Button size="sm" onClick={() => bulkApprove("approve")} className="gap-1.5 bg-green-600 hover:bg-green-700 whitespace-nowrap"><CheckCircle2 className="h-3.5 w-3.5" />Approve all</Button>}
        <Button size="sm" variant="outline" onClick={exportCSV} className="gap-1.5 whitespace-nowrap"><FileDown className="h-3.5 w-3.5" />Export CSV</Button>
        <Button size="sm" variant="outline" onClick={() => setBroadcast(true)} className="gap-1.5 whitespace-nowrap"><Send className="h-3.5 w-3.5" />Broadcast</Button>
      </div>

      {loading ? <div className="h-48 rounded-2xl shimmer" /> : regs.length === 0 ? (
        <EmptyState icon={Users} title="No registrations yet" />
      ) : (
        <div className="glass rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border/50">
                {["Name / Email","USN","Dept","Registered","Status",""].map(h => <th key={h} className="text-left text-xs text-muted-foreground font-medium px-4 py-2.5">{h}</th>)}
              </tr></thead>
              <tbody>
                {regs.map(r => (
                  <tr key={r.regID} className="border-b border-border/30 hover:bg-white/30 dark:hover:bg-white/5">
                    <td className="px-4 py-2.5"><div className="font-medium text-sm">{r.name}</div><div className="text-xs text-muted-foreground">{r.email}</div></td>
                    <td className="px-4 py-2.5 font-mono text-xs">{r.usn || "—"}</td>
                    <td className="px-4 py-2.5 text-xs">{r.dept || "—"}</td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground">{fmtDate(r.registeredAt)}</td>
                    <td className="px-4 py-2.5"><Badge status={r.status} /></td>
                    <td className="px-4 py-2.5">
                      <div className="flex gap-1">
                        {r.status === "pending_approval" && <>
                          <Button size="sm" className="h-6 text-[10px] px-2 bg-green-600 hover:bg-green-700" onClick={() => api.adminApproveReg(r.regID,"approve").then(reload).catch(e=>toast.error(e.message))}>✓</Button>
                          <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 text-destructive" onClick={() => api.adminApproveReg(r.regID,"reject").then(reload).catch(e=>toast.error(e.message))}>✗</Button>
                        </>}
                        {r.status !== "cancelled" && (
                          <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 text-destructive" onClick={() => api.adminRemoveReg(r.regID).then(reload).catch(e=>toast.error(e.message))} title="Remove"><X className="h-3 w-3" /></Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Broadcast modal */}
      <Dialog open={broadcast} onOpenChange={setBroadcast}>
        <DialogContent className="glass max-w-md">
          <DialogHeader><DialogTitle>Broadcast to all registrants</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Subject</Label><Input value={bSubject} onChange={e => setBSubject(e.target.value)} placeholder="Important update about the event" /></div>
            <div className="space-y-1.5"><Label>Message</Label><Textarea rows={5} value={bBody} onChange={e => setBBody(e.target.value)} placeholder="Your message…" /></div>
            <Button onClick={sendBroadcast} disabled={bBusy} className="w-full gap-2"><Send className="h-4 w-4" />{bBusy ? "Sending…" : "Send to all"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ── Analytics tab ───────────────────────────────────────────────── */
function Analytics({ bookingID }) {
  const { data, loading } = useFetch(() => api.getReport(bookingID), [bookingID]);
  const d = data || {};
  return (
    <div className="space-y-4">
      {loading ? <div className="h-40 rounded-2xl shimmer" /> : (
        <div className="surface rounded-2xl p-5 space-y-3">
          <p className="font-bold text-[15px]">Event report</p>
          {d.summary ? (
            <div className="grid grid-cols-2 gap-3 text-sm">
              {Object.entries(d.summary).map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-border/30 pb-1.5">
                  <span className="text-muted-foreground capitalize text-xs">{k.replace(/_/g, " ")}</span>
                  <span className="font-medium text-xs">{String(v)}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-muted-foreground">No report data yet. Appears after participants check in.</p>}
          {d.deptBreakdown && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wider">By department</p>
              <div className="space-y-1">
                {Object.entries(d.deptBreakdown).map(([dept, count]) => (
                  <div key={dept} className="flex justify-between text-xs py-1 border-b border-border/20">
                    <span>{dept || "Unknown"}</span><span className="font-medium">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Feedback tab ────────────────────────────────────────────────── */
function FeedbackTab({ bookingID }) {
  const { data, loading } = useFetch(() => api.listFeedback(bookingID), [bookingID]);
  const feedbacks = data?.feedbacks || [];
  return (
    <div className="space-y-3">
      {loading ? <div className="h-40 rounded-2xl shimmer" /> : feedbacks.length === 0 ? (
        <EmptyState icon={MessageSquare} title="No feedback yet" />
      ) : (
        <>
          {data?.averages && (
            <div className="glass rounded-2xl p-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {Object.entries(data.averages).map(([k, v]) => (
                <div key={k} className="text-center">
                  <div className="text-2xl font-bold text-primary">{Number(v).toFixed(1)}</div>
                  <div className="text-[10px] text-muted-foreground capitalize">{k.replace(/_/g," ")}</div>
                </div>
              ))}
            </div>
          )}
          <div className="space-y-2">
            {feedbacks.map((fb, i) => (
              <div key={i} className="glass rounded-xl p-3 space-y-1">
                {fb.comments && <p className="text-sm">"{fb.comments}"</p>}
                {fb.ratings && <div className="flex flex-wrap gap-2">{Object.entries(fb.ratings).map(([k,v]) => <span key={k} className="text-[10px] bg-primary/10 text-primary px-2 py-0.5 rounded-full">{k}: {v}/5</span>)}</div>}
                <p className="text-[10px] text-muted-foreground">{fb.isAnon ? "Anonymous" : fb.name} · {fmtDate(fb.submittedAt)}</p>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ── Post-event recap ────────────────────────────────────────────── */
function PostEvent({ bookingID }) {
  const { data } = useFetch(() => api.getPostEvent(bookingID), [bookingID]);
  const [form, setForm] = useState({ galleryUrl:"", recording:"", reportUrl:"", highlights:"", actualCount:"", outcome:"" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (data?.success) setForm({ galleryUrl: data.galleryUrl||"", recording: data.recording||"", reportUrl: data.reportUrl||"", highlights: data.highlights||"", actualCount: data.actualCount||"", outcome: data.outcome||"" });
  }, [data]);
  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }));
  const save = async () => {
    setBusy(true);
    try { await api.savePostEvent(bookingID, form); toast.success("Post-event recap saved"); }
    catch(e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="surface rounded-2xl p-5 space-y-4 max-w-lg">
      <p className="font-bold text-[15px]">Post-event recap</p>
      {[["galleryUrl","Gallery / album URL"],["recording","Recording URL"],["reportUrl","Report / documentation URL"],["highlights","Highlights & summary"],["actualCount","Actual attendee count"],["outcome","Event outcome"]].map(([k, label]) => (
        <div key={k} className="space-y-1.5">
          <Label>{label}</Label>
          {k === "highlights" || k === "outcome" ? (
            <Textarea rows={2} value={form[k]} onChange={f(k)} placeholder={label + "…"} />
          ) : (
            <Input value={form[k]} onChange={f(k)} placeholder={label + "…"} />
          )}
        </div>
      ))}
      <Button onClick={save} disabled={busy} className="gap-2"><Save className="h-4 w-4" />{busy ? "Saving…" : "Save recap"}</Button>
    </div>
  );
}

/* ── Certificates tab ────────────────────────────────────────────── */
function Certs({ bookingID }) {
  const [minDays, setMinDays] = useState(1);
  const [revokeID, setRevokeID] = useState("");
  const [busy, setBusy] = useState(false);
  const [closeBusy, setCloseBusy] = useState(false);
  const issue = async () => {
    setBusy(true);
    try { await api.issueCerts(bookingID, minDays); toast.success("Certificates issued and emailed!"); }
    catch(e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  const revoke = async () => {
    if (!revokeID) { toast.error("Enter a certificate ID"); return; }
    setBusy(true);
    try { await api.revokeCert(revokeID); toast.success("Certificate revoked"); setRevokeID(""); }
    catch(e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  const closeEvent = async () => {
    if (!window.confirm("Close this event? Registration and check-in will be locked.")) return;
    setCloseBusy(true);
    try { await api.closeEvent(bookingID); toast.success("Event closed"); }
    catch(e) { toast.error(e.message); }
    finally { setCloseBusy(false); }
  };
  return (
    <div className="space-y-5 max-w-lg">
      <div className="surface rounded-2xl p-5 space-y-4">
        <p className="font-bold text-[15px]">Issue certificates</p>
        <p className="text-xs text-muted-foreground">Emailed to all confirmed & checked-in participants with a public verify link.</p>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Label className="shrink-0 text-sm">Min. days attended</Label>
            <Input type="number" min={1} value={minDays} onChange={e => setMinDays(Math.max(1, Number(e.target.value)))} className="w-20 text-center" />
          </div>
          <Button onClick={issue} disabled={busy} className="gap-2"><Award className="h-4 w-4" />{busy ? "Issuing…" : "Issue now"}</Button>
        </div>
      </div>
      <div className="surface rounded-2xl p-5 space-y-3">
        <p className="font-bold text-[15px]">Revoke certificate</p>
        <div className="flex gap-2">
          <Input value={revokeID} onChange={e => setRevokeID(e.target.value)} placeholder="Certificate ID (e.g. CERT-BSN001-R001)" className="flex-1 font-mono text-xs" />
          <Button variant="outline" onClick={revoke} disabled={busy} className="gap-1.5 shrink-0 text-destructive"><X className="h-4 w-4" />Revoke</Button>
        </div>
      </div>
      <div className="surface rounded-2xl p-5 space-y-3 border border-amber-200 dark:border-amber-800/40">
        <p className="font-bold text-[15px] flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500" />Close event</p>
        <p className="text-xs text-muted-foreground">Locks registration and check-in. Cannot be undone.</p>
        <Button variant="outline" onClick={closeEvent} disabled={closeBusy} className="gap-2 text-destructive border-destructive/30 hover:bg-destructive/10">
          {closeBusy ? "Closing…" : "Close event permanently"}
        </Button>
      </div>
    </div>
  );
}

/* ── Check-in stats tab ──────────────────────────────────────────── */
function CheckinStats({ bookingID }) {
  const att = useFetch(() => api.attendance(bookingID), [bookingID]);
  const d = att.data || {};
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Checked in"  value={d.totalCheckins  || 0}    accent="#10B981" />
        <StatCard label="Attendance"  value={d.attendanceRate || "0%"}  accent="#0038C8" />
        <StatCard label="Walk-ins"    value={d.walkins        || 0}     accent="#F59E0B" />
        <StatCard label="Registered"  value={d.totalRegistered || 0}   accent="#8B5CF6" />
      </div>
      <div className="surface rounded-2xl p-5 space-y-3">
        <p className="text-sm font-medium">Open scanner on event day</p>
        <a href={`/checkin?id=${bookingID}`} target="_blank" rel="noopener noreferrer">
          <Button className="gap-2"><QrCode className="h-4 w-4" />Open check-in scanner ↗</Button>
        </a>
      </div>
    </div>
  );
}

/* ── Team access: owner + coordinators ──────────────────────────── */
const PERM_DEFS = [
  { id: "registrations", label: "Registrations", hint: "View list, approve, send invites & broadcasts" },
  { id: "checkin",       label: "Check-in",      hint: "Scan tickets & mark attendance / presence" },
  { id: "feedback",      label: "Feedback",      hint: "View & collect participant feedback" },
];

function TeamAccess({ bookingID }) {
  const { user } = useAuth();
  const { data, loading, error, reload } = useFetch(() => api.listEventTeam(bookingID), [bookingID]);
  const [email, setEmail] = useState("");
  const [perms, setPerms] = useState({ registrations: true, checkin: true, feedback: true });
  const [busy,  setBusy]  = useState(false);

  // Degrade gracefully if the Worker doesn't expose the team endpoints yet.
  const unsupported  = !!error && /unknown|invalid|not.?found|no such|action/i.test(error.message || "");
  const owner        = data?.owner || "";
  const coordinators = data?.coordinators || [];
  const iAmOwner     = !owner || (user?.email && owner.toLowerCase() === user.email.toLowerCase());

  const addCoord = async () => {
    const e = email.trim().toLowerCase();
    if (!e.includes("@")) { toast.error("Enter a valid email"); return; }
    const selected = Object.keys(perms).filter(k => perms[k]);
    if (selected.length === 0) { toast.error("Pick at least one permission"); return; }
    setBusy(true);
    try {
      await api.addCoordinator(bookingID, e, selected);
      toast.success(`${e} added as coordinator`);
      setEmail(""); reload();
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };

  const removeCoord = async (e) => {
    if (!window.confirm(`Remove ${e} from this event's team?`)) return;
    try { await api.removeCoordinator(bookingID, e); toast.success(`${e} removed`); reload(); }
    catch (err) { toast.error(err.message); }
  };

  if (loading) return <div className="space-y-3 max-w-2xl">{[...Array(2)].map((_, i) => <div key={i} className="h-20 rounded-2xl shimmer" />)}</div>;

  return (
    <div className="space-y-4 max-w-2xl">
      {unsupported && (
        <div className="surface rounded-2xl p-4 border border-amber-200 dark:border-amber-800/40 bg-amber-50/40 dark:bg-amber-950/20 text-xs text-amber-700 dark:text-amber-400 flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
          <span>Coordinator access needs the matching Worker actions (<code>listEventTeam</code>, <code>addEventCoordinator</code>, <code>removeEventCoordinator</code>). Until they're deployed this panel stays read-only.</span>
        </div>
      )}

      {/* Owner */}
      <div className="surface rounded-2xl p-5">
        <p className="overline mb-3">Event owner</p>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-blue-600/12 border border-blue-600/20 flex items-center justify-center text-blue-600 dark:text-blue-400"><Award className="h-4 w-4" /></div>
          <div className="min-w-0">
            <div className="text-sm font-semibold truncate">{owner || user?.email || "You"}</div>
            <div className="text-[11px] text-muted-foreground">Full control · cannot be removed</div>
          </div>
        </div>
      </div>

      {/* Coordinators list */}
      <div className="surface rounded-2xl p-5 space-y-3">
        <div className="flex items-center justify-between">
          <p className="overline">Coordinators</p>
          <span className="text-[11px] text-muted-foreground">{coordinators.length} added</span>
        </div>
        {coordinators.length === 0 ? (
          <p className="text-xs text-muted-foreground">No coordinators yet. Add teammates below to help run this event.</p>
        ) : coordinators.map(c => (
          <div key={c.email} className="flex items-start gap-3 py-2 border-b border-border/30 last:border-0">
            <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center text-xs font-bold shrink-0">{(c.email || "?")[0].toUpperCase()}</div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{c.email}</div>
              <div className="flex flex-wrap gap-1 mt-1">
                {(c.perms || []).map(p => <span key={p} className="text-[10px] px-2 py-0.5 rounded-full bg-blue-600/10 text-blue-600 dark:text-blue-400 capitalize">{p}</span>)}
              </div>
            </div>
            {iAmOwner && !unsupported && (
              <button onClick={() => removeCoord(c.email)} className="text-red-500 hover:text-red-600 p-1 shrink-0" title="Remove"><Trash2 className="h-4 w-4" /></button>
            )}
          </div>
        ))}
      </div>

      {/* Add coordinator */}
      {iAmOwner && (
        <div className="surface rounded-2xl p-5 space-y-4">
          <p className="overline">Add a coordinator</p>
          <div className="space-y-1.5">
            <Label>Staff email</Label>
            <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="coordinator@bmsit.in" disabled={unsupported} />
          </div>
          <div className="space-y-2">
            <Label>What can they do?</Label>
            {PERM_DEFS.map(p => (
              <label key={p.id} className="flex items-start gap-3 p-2.5 rounded-xl border border-border cursor-pointer hover:border-blue-300 dark:hover:border-blue-700/60">
                <input type="checkbox" checked={!!perms[p.id]} onChange={e => setPerms(s => ({ ...s, [p.id]: e.target.checked }))} className="mt-1 accent-blue-600" disabled={unsupported} />
                <div>
                  <div className="text-sm font-medium">{p.label}</div>
                  <div className="text-[11px] text-muted-foreground">{p.hint}</div>
                </div>
              </label>
            ))}
          </div>
          <Button onClick={addCoord} disabled={busy || unsupported} className="gap-2 w-full"><Plus className="h-4 w-4" />{busy ? "Adding…" : "Add coordinator"}</Button>
        </div>
      )}
    </div>
  );
}

/* ── Manage event ────────────────────────────────────────────────── */
function ManageEvent({ id }) {
  const fetcher = () => id.startsWith("EVT-") ? api.getStandaloneEvent(id) : api.getEventProfile(id).catch(() => api.getStandaloneEvent(id));
  const ev = useFetch(fetcher, [id]);
  if (ev.loading) return <Shell role="portal" title="Loading…"><div className="space-y-4 max-w-2xl">{[...Array(4)].map((_, i) => <div key={i} className="h-24 rounded-2xl shimmer" />)}</div></Shell>;
  if (!ev.data) return <Shell role="portal" title="Not found"><EmptyState icon={Megaphone} title="Event not found" description={id} /></Shell>;
  const evData = ev.data;
  return (
    <Shell role="portal" title={evData.title || "Edit event"} subtitle={evData.isStandalone ? `EVT · ${id}` : `Booking · ${id}`}
      actions={evData.eventUrl && <a href={evData.eventUrl} target="_blank" rel="noopener noreferrer"><Button size="sm" variant="outline" className="gap-1.5 text-xs"><Globe className="h-3.5 w-3.5" />Public page</Button></a>}
    >
      <Tabs defaultValue="profile" className="space-y-5">
        <div className="overflow-x-auto"><TabsList className="glass p-1 h-auto gap-1 whitespace-nowrap">
          {[["profile","Profile"],["team","Team"],["registrations","Participants"],["checkin","Check-in"],["analytics","Analytics"],["feedback","Feedback"],["postevent","Post-event"],["certificates","Certificates"]].map(([v,l]) => (
            <TabsTrigger key={v} value={v} className="text-xs px-3">{l}</TabsTrigger>
          ))}
        </TabsList></div>
        <TabsContent value="profile"><ProfileForm eventID={evData.isStandalone ? id : undefined} bookingID={evData.isStandalone ? undefined : id} initial={evData} /></TabsContent>
        <TabsContent value="team"><TeamAccess bookingID={id} /></TabsContent>
        <TabsContent value="registrations"><Registrations bookingID={id} /></TabsContent>
        <TabsContent value="checkin"><CheckinStats bookingID={id} /></TabsContent>
        <TabsContent value="analytics"><Analytics bookingID={id} /></TabsContent>
        <TabsContent value="feedback"><FeedbackTab bookingID={id} /></TabsContent>
        <TabsContent value="postevent"><PostEvent bookingID={id} /></TabsContent>
        <TabsContent value="certificates"><Certs bookingID={id} /></TabsContent>
      </Tabs>
    </Shell>
  );
}

function ManageWrapper()   { const { eventID }   = useParams(); return <ManageEvent id={eventID} />; }
function BookingWrapper()  { const { bookingID } = useParams(); return <Shell role="portal" title="Set up event"><ProfileForm bookingID={bookingID} /></Shell>; }
function NewWrapper()      { return <Shell role="portal" title="Create new event"><ProfileForm /></Shell>; }

export default function HostEvent() {
  return (
    <Routes>
      <Route index element={<EventList />} />
      <Route path="new" element={<NewWrapper />} />
      <Route path="booking/:bookingID" element={<BookingWrapper />} />
      <Route path="manage/:eventID" element={<ManageWrapper />} />
    </Routes>
  );
}
